import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import Editor, { Monaco } from '@monaco-editor/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useLazyQuery } from '@apollo/client'
import { ChevronLeftIcon } from '@ui/components/icons'
import { SimulateEventPipelineQL } from '@ui/loaders/processor'
import { useCodeNodeStore, useNodeAndEdgeStore, useTelemetryStore } from '@ui/stores'
import clsx from 'clsx'
import debounce from 'lodash/debounce'

import { DiffView } from './DiffView'

interface CodeProps {
  selectedEvent: any
  events: any[]
  setParentCode: (value: string) => void
  setEditorModified: (value: boolean) => void
}

export function Code({ setParentCode, setEditorModified }: CodeProps) {
  const nodes = useNodeAndEdgeStore.getState().nodes
  const edges = useNodeAndEdgeStore.getState().edges
  const currentNodeId = useNodeAndEdgeStore(useShallow((state) => state.currentNodeId)) as string
  const code = useCodeNodeStore(useShallow((state) => state.code))
  const selectedTable = useCodeNodeStore(useShallow((state) => state.table))

  const { dataSource, logs, spans, selectedEvent } = useTelemetryStore(
    useShallow((state) => ({
      dataSource: state.dataSource,
      logs: state.logs,
      spans: state.spans,
      selectedEvent: state.selectedEvent,
    }))
  )

  const [localCode, setLocalCode] = useState<string | null>(null)
  const [consoleExpanded, setConsoleExpanded] = useState<boolean>(true)

  const codeRef = useRef<string | null>('')

  const onCodeChange = (value: string | undefined) => {
    if (codeRef.current !== value) {
      setEditorModified(true)
    } else {
      setEditorModified(false)
    }

    setLocalCode(value || '')
    debouncer({
      variables: {
        simulateEventParams: {
          selectedNodeId: currentNodeId,
          selectedNodeCode: value || '',
          selectedEvent: JSON.parse(JSON.stringify(selectedEvent)),
          selectedEventType: dataSource === 'logs' ? 'log' : 'span',
          events: dataSource === 'logs' ? logs : spans,
          nodes: nodes,
          edges: edges,
        },
      },
    })
  }

  const metadata = useMemo(() => {
    return {
      datatype: selectedTable === 'logs' ? 'log' : 'span',
    }
  }, [selectedTable])

  const [search, { data }] = useLazyQuery(SimulateEventPipelineQL)

  const debouncer = useCallback(debounce(search, 500), [])

  const { output, consoleLog, errorLog } = data?.pipeline?.simulateEvent?.selectedEvent || {}

  const updateParent = useCallback(
    debounce(
      (newCode) => {
        setParentCode(newCode)
      },
      600,
      { trailing: true }
    ),
    []
  )

  // trigger debounce to update the input/output components every time localCode updates
  useEffect(() => {
    updateParent(localCode)
  }, [updateParent, localCode])

  // re-run the simulation every time the event changes
  useEffect(() => {
    debouncer({
      variables: {
        simulateEventParams: {
          selectedNodeId: currentNodeId,
          selectedNodeCode: localCode || '',
          selectedEvent: JSON.parse(JSON.stringify(selectedEvent || {})),
          selectedEventType: dataSource === 'logs' ? 'log' : 'span',
          events: dataSource === 'logs' ? logs : spans,
          nodes: nodes,
          edges: edges,
        },
      },
    })
  }, [debouncer, selectedEvent, localCode, metadata, currentNodeId, dataSource, logs, spans, nodes, edges])

  useEffect(() => {
    if (code) {
      setLocalCode(code)
    }
  }, [code])

  const handleEditorDidMount = (monaco: Monaco) => {
    monaco.editor.defineTheme('onedark', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#ffffff',
      },
    })
  }

  return (
    <div className="flex w-full flex-col">
      <div
        className={
          'relative w-full flex-row border-x border-t border-gray-300 bg-white py-2.5 pl-3 text-base font-semibold'
        }>
        Javascript
        <div
          className={clsx('absolute right-0 top-0 flex h-full flex-row items-center border-gray-300 p-2.5', {
            'w-2/5 border-l': consoleExpanded,
            'w-1/5 justify-end': !consoleExpanded,
          })}>
          Console Log
          {(consoleLog || errorLog) && (
            <ExclamationTriangleIcon
              className={clsx('pulse-animation ml-4 h-5 w-5', {
                'text-red-600': errorLog,
                'text-yellow-600': !errorLog && consoleLog,
              })}
            />
          )}
        </div>
      </div>
      <div className="relative flex flex-row">
        <Editor
          height="38vh"
          width={consoleExpanded ? '60%' : '100%'}
          theme="onedark"
          defaultLanguage="javascript"
          value={localCode || ''}
          onChange={onCodeChange}
          className="border-x border-b border-gray-300"
          beforeMount={handleEditorDidMount}
        />

        <pre
          className={clsx(
            'absolute right-0 h-full overflow-y-scroll border-b border-r border-gray-300 bg-white p-2.5 transition-all duration-150',
            {
              'w-2/5': consoleExpanded,
              'w-0': !consoleExpanded,
              'text-red-600': errorLog,
            }
          )}>
          {consoleLog && consoleLog.length > 0 ? consoleLog.join('\n') + '\n' : ''}
          {errorLog}
          <ChevronLeftIcon
            className={clsx(
              'absolute left-0 top-44 h-5 w-5 cursor-pointer stroke-gray-400 transition-all duration-150 hover:stroke-primary-500',
              {
                'rotate-180': consoleExpanded,
              }
            )}
            onClick={() => {
              setConsoleExpanded(!consoleExpanded)
            }}
          />
        </pre>
      </div>

      <DiffView
        input={selectedEvent}
        output={errorLog && errorLog.length > 0 ? undefined : output}
        height={consoleExpanded ? '32dvh' : '32dvh'}
      />
    </div>
  )
}
